import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { resetMainSensor, updateSorting } from '../../store/slices/sensors';
import { useGetLocationsQuery } from '../../store/api/sensors';
import { useGetFieldsQuery } from '../../store/api/fields';
import { Header, Loading } from '../../components';
import { EmptyView } from '../../components/EmptyView/index.jsx';
import { Sensors } from '../../components/Sensors/index.jsx';
import { getSidebarLinksMap } from '../../utils/ui';

const SensorsPage = () => {
  const { t } = useTranslation();
  const SidebarLinksMap = getSidebarLinksMap(t);

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    selectedDate,
    sorting: { dir, parameter, crop },
  } = useSelector((state) => state.sensors);
  const { currentWorkspaceId } = useSelector((state) => state.workspaces);
  const { crops } = useSelector((state) => state.catalog);

  const [cropsList, setCropsList] = useState([]);
  const [seasonsList, setSeasonsList] = useState([]);
  const [extendedData, setExtendedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const {
    data: locations,
    isSuccess: isLocationsSuccess,
    isError: isLocationsError,
    isFetching: isLocationsLoading,
    error: cropsError,
  } = useGetLocationsQuery({}, { skip: !currentWorkspaceId, refetchOnMountOrArgChange: true });

  const { data: fields, isSuccess: isFieldsSuccess, isFetching: isFieldsLoading } = useGetFieldsQuery();

  useEffect(() => {
    dispatch(resetMainSensor());

    if (selectedDate && !searchParams.get('date')) {
      searchParams.set('date', selectedDate);
    }

    if (searchParams.get('tab')) {
      dispatch(updateSorting({ dir, parameter, crop: searchParams.get('tab') }));
    } else if (crop !== 'all') {
      searchParams.set('tab', crop);
    }

    if (!searchParams.get('sort')) {
      searchParams.set('sort', dir + parameter);
    }

    setSearchParams(searchParams);
  }, []);

  useEffect(() => {
    if (!isFieldsSuccess || !isLocationsSuccess) {
      return;
    }

    const seasons = [];
    const sensorsExtendedData = [];
    const cropsCurrent = [];

    fields.map((obj) => {
      let list = obj.seasons;
      if (list) {
        list = list.map((l) => {
          return {
            ...l,
            fieldName: obj.name,
          };
        });
        seasons.push(...list);
      }
    });

    locations.map((obj) => {
      const season = seasons.filter((s) => s.id === obj.field_season_id)[0];
      if (season && !cropsCurrent.find((item) => item.id === season.crop_id)) {
        cropsCurrent.push({
          id: season.crop_id,
          color:
            season.crop_id !== 'all' ? crops.filter((item) => item.id === season.crop_id)[0]?.color : 'transparent',
        });
      }
      sensorsExtendedData.push({ ...obj, fieldName: season ? season.fieldName : null });
    });

    setCropsList(cropsCurrent);
    setSeasonsList(seasons);
    setExtendedData(sensorsExtendedData);
  }, [isFieldsSuccess, isLocationsSuccess]);

  useEffect(() => {
    if (isLocationsError && cropsError) {
      setErrorMessage(cropsError.data.errors[0].detail);
    }
  }, [isLocationsError]);

  const isLoading = useMemo(() => isLocationsLoading || isFieldsLoading, [isLocationsLoading, isFieldsLoading]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <Header title={SidebarLinksMap.sensors.title} t={t} />
      <main className="main" key={currentWorkspaceId}>
        {!isLoading && isLocationsSuccess && isFieldsSuccess && locations && locations.length ? (
          <Sensors t={t} seasons={seasonsList} currentCrops={cropsList} sensorsData={extendedData} />
        ) : (
          <EmptyView message={errorMessage} t={t} />
        )}
      </main>
    </>
  );
};

export default SensorsPage;
